<template>
    <OwnerAddDialog v-model="dialogVisible" />
    <div class="events-edit-view flex flex-column w-full gap-5 h-full justify-content-start">
        <h2 class="events-edit-header">{{ $t('message.horseEdit.events.header') }} ({{ horse.name }})</h2>
        <div class="section-table w-full">
            <DataTable :value="events" lazy removableSort
                       :loading="loading" class="edit-horse-events-table">
                <Column field="date" :header="$t('message.horseEdit.events.date')" sortable class="w-2">
                </Column>
                <Column field="type.description" :header="$t('message.horseEdit.events.name')" sortable class="w-2">
                </Column>
                <Column field="description" :header="$t('message.horseEdit.events.description')" class="w-4">
                </Column>
            </DataTable>
        </div>
        <div class="section-details flex flex-column gap-5">
            <div class="flex justify-content-between">
                <h3 class="uppercase">Add event</h3>
                <CustomSelectOne v-if="loadedEventTypes" name="eventType" :label="$t('message.horseEdit.events.eventType')"
                                 class="w-4 event-type-select" :items="eventTypes" v-model="selectedEventType" required
                                 :customErrorMessage="getRequiredMessage(selectedEventType)" />
            </div>
            <div>
                <div class="flex justify-content-center gap-4 w-full">
                    <CustomCalendar name="eventDate" :label="$t('message.horseEdit.events.eventDate')" class="w-4 event-date-select align-self-start"
                                    :style="selectedEventType?.label === 'Change of owner' ? 'margin-top: 41px;' : ''" v-model="newEvent.date"
                                    required :customErrorMessage="getRequiredMessage(newEvent.date)" />
                    <div v-if="selectedEventType?.label === 'Change of owner'" class="flex flex-column w-4 gap-3">
                        <span>*{{ $t('message.horseEdit.events.newOwners') }}:</span>
                        <div v-for="(owner,index) in newEvent.newOwners" :key="index" class="flex w-full flex-column gap-2">
                            <div class="relative">
                                <CustomAutocompleteOwner name="newOwners" class="new-owners-select flex-column" v-model="newEvent.newOwners[index]"
                                                      :key="dialogVisible" itemValue="id" :customErrorMessage="getRequiredMessage(newEvent.newOwners[index])" />
                                <CustomIconButton icon="pi pi-trash" onlyIcon @click="removeOwner(index)" class="delete-owner-button absolute" />
                            </div>
                            <div v-if="index === newEvent?.newOwners?.length - 1 && owner == null" class="align-self-end ml-5">
                                <CustomTextButton class="create-new" :label="$t('message.horseEdit.events.createNew')"
                                                  @click="openDialog"/>
                            </div>
                        </div>
                        <CustomTextButton v-if="!lastOwnerNull" :label="$t('message.horseEdit.events.addOwner')"
                                          @click="addOwner" class="align-self-start" />
                    </div>
                    <div v-else-if="selectedEventType?.label === 'Export'" class="flex w-4 flex-column gap-2">
                        <CustomSelectOneCountry name="export" :label="$t('message.horseEdit.events.export')" v-model="newEvent.countryId"
                                                itemValue="id" :customErrorMessage="getRequiredMessage(newEvent.countryId)" filter />
                        <CustomInputText name="fullName" v-model="newEvent.additionalInformation"
                                         :label="$t('message.horseEdit.events.newOwners')" rules="required"/>
                    </div>
                    <div v-else class="dumb w-4"></div>
                </div>
            </div>
            <CustomButton :label="$t('message.horseEdit.events.add')" class="align-self-center"
                          @click="createEvent" />
        </div>
    </div>
</template>

<script>
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import {Form} from "vee-validate";
import {
    getEvents, createEvent, getAllEventTypesToSelectValues,
} from "@/swagger/vue-api-client";
import LibyanOrnament from "@/components/LibyanOrnament.vue";
import CustomButton from "@/components/form/CustomButton.vue";
import CustomIconButton from "@/components/form/CustomIconButton.vue";
import CustomTextButton from "@/components/form/CustomTextButton.vue";
import CustomSelectOne from "@/components/form/inner/CustomSelectOne.vue";
import CustomCalendar from "@/components/form/CustomCalendar.vue";
import OwnerForm from "@/views/owner/components/OwnerForm.vue";
import OwnerAddDialog from "@/views/owner/OwnerAddDialog.vue";
import CustomSelectOneCountry from "@/components/form/CustomSelectOneCountry.vue";
import { ValidateUtils } from "@/utils/ValidateUtils";
import CustomInputText from "@/components/form/CustomInputText.vue";
import CustomAutocompleteOwner from "@/components/form/CustomAutocompleteOwner.vue";

export default {
    name: "HorseEditEventsView",

    components: {
        CustomAutocompleteOwner,
        CustomInputText,
        CustomIconButton,
        CustomButton,
        CustomTextButton,
        LibyanOrnament,
        Form,
        CustomSelectOne,
        Column,
        DataTable,
        CustomCalendar,
        OwnerForm,
        OwnerAddDialog,
        CustomSelectOneCountry,
    },

    props: {
        horse: null,
    },

    emits: ["refreshHorse"],

    data() {
        return {
            loading: false,
            events: [],
            event: null,
            eventTypes: [],
            loadedEventTypes: false,
            dialogVisible: false,
            selectedEventType: null,
            newEvent: this.getClearNewEvent(),
            showErrorMessages: false,
        };
    },

    computed: {
        lastOwnerNull() {
            if (this.newEvent.newOwners == null || this.newEvent.newOwners.length === 0) return false;
            const lastIndex = this.newEvent.newOwners.length - 1;
            return this.newEvent.newOwners[lastIndex] == null;
        },
    },
    beforeMount() {
        this.getEvents();
        this.getAllEventTypes();
    },

    mounted() {
        this.addOwnerIfThereIsNoOwner();
    },

    methods: {
        getAllEventTypes() {
            getAllEventTypesToSelectValues().then((response) => {
                this.eventTypes = response.data;
                if ((this.horse.sex)?.toUpperCase() !== "STALLION") {
                    const index = this.eventTypes.map((e) => e.label).indexOf("GELDING");
                    this.eventTypes.splice(index, 1);
                }
                this.loadedEventTypes = true;
            });
        },
        getEvents() {
            this.loading = true;
            const horseId = this.horse.id || this.$route.params.id;
            getEvents({horseId}).then((res) => {
                this.events = res.data;
                this.loading = false;
            });
        },
        validateData() {
            let isCorrect = ValidateUtils.validateNotEmpty(this.newEvent, ["typeId", "date"]);
            if (this.selectedEventType?.label === "Change of owner") {
                isCorrect = ValidateUtils.validateNotEmpty(this.newEvent, ["newOwners"]) && this.newEvent.newOwners.length > 0 && isCorrect;
            }
            if (this.selectedEventType?.label === "Export") {
                isCorrect = ValidateUtils.validateNotEmpty(this.newEvent, ["countryId"]) && isCorrect;
            }
            this.showErrorMessages = !isCorrect;
            return isCorrect;
        },
        createEvent() {
            if (this.validateData()) {
                const horseId = this.horse.id || this.$route.params.id;

                createEvent({horseId, body: this.newEvent})
                    .then(() => {
                        this.$toast.add({
                            severity: "success",
                            summary: this.$t("message.other.success"),
                            detail: this.$t("message.other.dataSaved"),
                            life: 3000,
                        });
                        this.getEvents();
                        this.selectedEventType = null;
                        this.newEvent = this.getClearNewEvent();
                        this.$emit("refreshHorse");
                    })
                    .catch((error) => {
                        const message = error.response?.data?.message || this.$t("unknownError");
                        this.$toast.add({
                            severity: "error",
                            summary: this.$t("message.other.error"),
                            detail: message,
                            life: 5000,
                        });
                    });
            }
        },
        addOwner() {
            this.newEvent.newOwners.push(null);
        },
        removeOwner(index) {
            this.newEvent.newOwners.splice(index, 1);
            this.addOwnerIfThereIsNoOwner();
        },
        addOwnerIfThereIsNoOwner() {
            if (this.newEvent.newOwners.length === 0) {
                this.addOwner();
            }
        },
        openDialog() {
            this.dialogVisible = true;
        },
        getRequiredMessage(value) {
            if (this.showErrorMessages && (value === null || value === "")) {
                return this.$t("message.validations.required");
            }
            return "";
        },
        getClearNewEvent() {
            return {
                typeId: null,
                date: null,
                horseSex: null,
                twins: null,
                pregnant: null,
                cover: null,
                horseSecondaryId: null,
                horseOffspringId: null,
                countryId: null,
                newOwners: [],
                additionalInformation: null,
            };
        },
    },

    watch: {
        selectedEventType: {
            handler(value) {
                if (value) {
                    this.newEvent.typeId = Number(value.value);
                }
            },
            deep: true,
        },
    },
};
</script>

<style lang="scss">
    .events-edit-view {
        .events-edit-header {
            color: var(--accent-color);
            font-weight: 500;
            font-size: 35px;
            font-family: var(--decor-font-family);
        }
        .section-details {
            background-color: var(--secondary-color);
            padding: 20px;
            border-radius: 4px;
            .custom-input-text, .custom-input-number, .custom-select-one:not(.new-owners-select), .custom-calendar {
                padding-left: 170px;
                .custom-label {
                    z-index: 999;
                    position: absolute;
                    transform: translateX(-100%);
                    left: 160px;
                    text-align: right;
                }
            }
            .new-owners-select {
                align-items: start !important;
            }
            .event-date-select {
                // margin-top: 41px;
            }
            .event-type-select {
                .custom-label {
                    text-align: right;
                    width: inherit;
                }
            }
        }
        .delete-owner-button {
            bottom: 12px;
            right: -40px;
        }
    }
</style>
